import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import SuccessModal from './SuccessModal';

const CardiacAuscultationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 12px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  width: 100%;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #6D6D71;
`;

const ContentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
`;

const ValveSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C5C5C6;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    width: calc(50% - 10px);
  }
`;

const ValveTitle = styled.h3`
  font-family: 'Sofia Sans', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
  margin: 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #E2E2E3;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;

  @media (min-width: 480px) {
    width: calc(50% - 8px);
  }
`;

const FormLabel = styled.label`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
`;

const Select = styled.select`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  width: 100%;
  height: 40px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;

  &::placeholder {
    color: #A7A7AA;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
  cursor: pointer;
  width: 100%;

  @media (min-width: 480px) {
    width: calc(50% - 8px);
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  cursor: pointer;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #E2E2E3;
  margin: 16px 0;
`;

const RadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  font-family: 'Sofia Sans', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
  cursor: pointer;
`;

const RadioInput = styled.input`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  cursor: pointer;
`;

// Mandatory fields carry the same red asterisk the demographics form uses.
const RequiredMark = styled.span`
  color: #D0342C;
  margin-right: 4px;
`;

const FieldError = styled.div`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 12px;
  line-height: 15px;
  color: #D0342C;
  margin-top: 4px;
`;

const SectionLabel = styled.div`
  font-family: 'Sofia Sans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: #6D6D71;
  margin-bottom: 8px;
`;

const SaveButton = styled.button`
  display: flex;
  width: 230px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 15px 15px;
  gap: 12px;
  background: #2575BD;
  border: none;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: #FFFFFF;
  cursor: pointer;
  margin-top: 20px;
  &:hover {
    background-color: ${props => props.disabled ? '#E0F0FF' : '#1c5c94'};
  }
`;

// Murmur presence is mandatory on the mitral and aortic valves. Every other field
// in this section is optional, so a form left untouched used to be indistinguishable
// from an examination that found nothing.
const MURMUR_PRESENCE_REQUIRED = ['mitral_valve', 'aortic_valve'];

const ValveComponent = ({ valve, title, data, onChange, showErrors }) => {
  const renderSelect = (field, label, options) => {
    const value = (data[field] || '').toLowerCase();

    return (
      <FormField>
        <FormLabel>{label}</FormLabel>
        <Select
          value={value}
          onChange={(e) => onChange(valve, field, e.target.value)}
        >
          <option value="">Select</option>
          {options.map((option) => (
            <option
              key={option}
              value={option.toLowerCase()}
            >
              {option}
            </option>
          ))}
        </Select>
      </FormField>
    );
  };

  const renderCheckbox = (field, label) => (
    <CheckboxLabel>
      <Checkbox
        type="checkbox"
        checked={data[field] || false}
        onChange={(e) => onChange(valve, field, e.target.checked)}
      />
      {label}
    </CheckboxLabel>
  );

  const handleNoMurmurToggle = (flagField, typeField, gradeField) => (e) => {
    onChange(valve, { [flagField]: e.target.checked, [typeField]: '', [gradeField]: '' });
  };

  const renderNoMurmurCheckbox = (flagField, typeField, gradeField, label) => (
    <CheckboxLabel>
      <Checkbox
        type="checkbox"
        checked={data[flagField] || false}
        onChange={handleNoMurmurToggle(flagField, typeField, gradeField)}
      />
      {label}
    </CheckboxLabel>
  );

  const isMurmurRequired = MURMUR_PRESENCE_REQUIRED.includes(valve);
  const murmurMissing = isMurmurRequired && !data.murmur_presence;

  return (
    <ValveSection>
      <ValveTitle>{title}</ValveTitle>
      <FormField>
        <FormLabel>
          {isMurmurRequired && <RequiredMark>*</RequiredMark>}
          Murmur
        </FormLabel>
        <RadioRow>
          {['Present', 'Absent'].map((option) => (
            <RadioLabel key={option}>
              <RadioInput
                type="radio"
                name={`${valve}_murmur_presence`}
                value={option}
                checked={data.murmur_presence === option}
                onChange={() => onChange(valve, 'murmur_presence', option)}
              />
              {option}
            </RadioLabel>
          ))}
        </RadioRow>
        {showErrors && murmurMissing && (
          <FieldError>Select whether a murmur is present or absent.</FieldError>
        )}
      </FormField>
      <Divider />
      <FieldGroup>
        {renderSelect('s1', 'S1', ['Normal', 'Loud', 'Soft', 'Muffled'])}
        {renderSelect('s2', 'S2', ['Normal', 'Loud', 'Soft', 'Muffled'])}
      </FieldGroup>
      <FieldGroup>
        {renderCheckbox('s1_split', 'S1 Split')}
        {renderCheckbox('s2_split', 'S2 Split')}
      </FieldGroup>
      <Divider />
      <SectionLabel>Additional sounds:</SectionLabel>
      <FieldGroup>
        {renderCheckbox('a2_loud', 'A2 Loud')}
        {renderCheckbox('a2_soft', 'A2 Soft')}
        {renderCheckbox('p2_loud', 'P2 Loud')}
        {renderCheckbox('p2_soft', 'P2 Soft')}
        {renderCheckbox('s3', 'S3')}
        {renderCheckbox('s4', 'S4')}
        {renderCheckbox('mid_systolic_click', 'Mid Systolic Click')}
        {renderCheckbox('opening_snap', 'Opening Snap')}
        {renderCheckbox('pericardial_rub', 'Pericardial Rub')}
        {renderCheckbox('ejection_click', 'Ejection Click')}
      </FieldGroup>
      <Divider />
      <SectionLabel>Murmurs:</SectionLabel>
      <FieldGroup>
        {renderNoMurmurCheckbox('no_systolic_murmur', 'systolic_murmur', 'systolic_grade', 'No Systolic Murmur')}
        {renderNoMurmurCheckbox('no_diastolic_murmur', 'diastolic_murmur', 'diastolic_grade', 'No Diastolic Murmur')}
      </FieldGroup>
      {!data.no_systolic_murmur && (
        <FieldGroup>
          {renderSelect('systolic_murmur', 'Systolic Murmur', ['Ejection', 'Early', 'Late', 'Pan'])}
          {data.systolic_murmur &&
            renderSelect('systolic_grade', 'Systolic Grade', ['I', 'II', 'III', 'IV', 'V', 'VI'])}
        </FieldGroup>
      )}
      {!data.no_diastolic_murmur && (
        <FieldGroup>
          {renderSelect('diastolic_murmur', 'Diastolic Murmur', ['Early', 'Mid', 'Late'])}
          {data.diastolic_murmur &&
            renderSelect('diastolic_grade', 'Diastolic Grade', ['I', 'II', 'III', 'IV', 'V', 'VI'])}
        </FieldGroup>
      )}
      <FieldGroup>
        {renderCheckbox('continuous_murmur', 'Continuous Murmur')}
        {data.continuous_murmur && 
          renderSelect('continuous_grade', 'Continuous Grade', ['I', 'II', 'III', 'IV', 'V', 'VI'])}
      </FieldGroup>
      {(valve === 'apex' || valve === 'axilla') && (
        <>
          <Divider />
          <FieldGroup>
            {renderCheckbox('radiation_to_apex', 'Radiation to Apex')}
            {renderCheckbox('radiation_to_axilla', 'Radiation to Axilla')}
          </FieldGroup>
        </>
      )}
    </ValveSection>
  );
};

const CardiacAuscultation = ({ participantId, formData, onFormDataChange, onSaveSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [data, setData] = useState({
    mitral_valve: {
      murmur_presence: '',
      s1: 'normal',
      s2: 'normal',
      s1_split: false,
      s2_split: false,
      s3: false,
      s4: false,
      mid_systolic_click: false,
      opening_snap: false,
      pericardial_rub: false,
      ejection_click: false,
      no_systolic_murmur: false,
      no_diastolic_murmur: false,
      systolic_murmur: '',
      systolic_grade: '',
      diastolic_murmur: '',
      diastolic_grade: '',
      continuous_murmur: false,
      continuous_grade: ''
    },
    aortic_valve: {
      murmur_presence: '',
      s1: 'normal',
      s2: 'normal',
      s1_split: false,
      s2_split: false,
      s3: false,
      s4: false,
      mid_systolic_click: false,
      opening_snap: false,
      pericardial_rub: false,
      no_systolic_murmur: false,
      no_diastolic_murmur: false,
      systolic_murmur: '',
      systolic_grade: '',
      diastolic_murmur: '',
      diastolic_grade: '',
      continuous_murmur: false,
      continuous_grade: '',
      a2_loud: false,
      a2_soft: false,
      p2_loud: false,
      p2_soft: false
    },
    axilla: {
      murmur_presence: '',
      s1: 'normal',
      s2: 'normal',
      s1_split: false,
      s2_split: false,
      s3: false,
      s4: false,
      mid_systolic_click: false,
      opening_snap: false,
      pericardial_rub: false,
      ejection_click: false,
      no_systolic_murmur: false,
      no_diastolic_murmur: false,
      systolic_murmur: '',
      systolic_grade: '',
      diastolic_murmur: '',
      diastolic_grade: '',
      continuous_murmur: false,
      continuous_grade: '',
      radiation_to_apex: false,
      radiation_to_axilla: false
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    const fetchCardiacAuscultation = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}participant/get-participant-info?participant_id=${participantId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          const apiData = response.data;
          const initialData = {
            mitral_valve: apiData.cardiac_auscultation?.mitral_valve || {
              murmur_presence: '',
              s1: 'normal',
              s2: 'normal',
              s1_split: false,
              s2_split: false,
              s3: false,
              s4: false,
              mid_systolic_click: false,
              opening_snap: false,
              pericardial_rub: false,
              ejection_click: false,
              no_systolic_murmur: false,
              no_diastolic_murmur: false,
              systolic_murmur: '',
              systolic_grade: '',
              diastolic_murmur: '',
              diastolic_grade: '',
              continuous_murmur: false,
              continuous_grade: ''
            },
            aortic_valve: apiData.cardiac_auscultation?.aortic_valve || {
              murmur_presence: '',
              s1: 'normal',
              s2: 'normal',
              s1_split: false,
              s2_split: false,
              s3: false,
              s4: false,
              mid_systolic_click: false,
              opening_snap: false,
              pericardial_rub: false,
              ejection_click: false,
              no_systolic_murmur: false,
              no_diastolic_murmur: false,
              systolic_murmur: '',
              systolic_grade: '',
              diastolic_murmur: '',
              diastolic_grade: '',
              continuous_murmur: false,
              continuous_grade: '',
              a2_loud: false,
              a2_soft: false,
              p2_loud: false,
              p2_soft: false
            },
            axilla: apiData.cardiac_auscultation?.axilla || {
              murmur_presence: '',
              s1: 'normal',
              s2: 'normal',
              s1_split: false,
              s2_split: false,
              s3: false,
              s4: false,
              mid_systolic_click: false,
              opening_snap: false,
              pericardial_rub: false,
              ejection_click: false,
              no_systolic_murmur: false,
              no_diastolic_murmur: false,
              systolic_murmur: '',
              systolic_grade: '',
              diastolic_murmur: '',
              diastolic_grade: '',
              continuous_murmur: false,
              continuous_grade: '',
              radiation_to_apex: false,
              radiation_to_axilla: false
            }
          };
          
          setData(initialData);
          onFormDataChange(initialData);
        }
      } catch (error) {
        console.error('Error fetching cardiac auscultation data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCardiacAuscultation();
  }, [participantId]);

  const handleChange = (section, fieldOrUpdates, value) => {
    const updates = typeof fieldOrUpdates === 'object'
      ? fieldOrUpdates
      : { [fieldOrUpdates]: value };
    const updatedData = {
      ...data,
      [section]: {
        ...data[section],
        ...updates
      }
    };
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const missingMurmurPresence = MURMUR_PRESENCE_REQUIRED.filter((valve) => !data[valve]?.murmur_presence);

  const handleSaveAndNext = async () => {
    // The server rejects this too (update-cardiac returns 422); blocking here just
    // saves the round trip.
    if (missingMurmurPresence.length > 0) {
      setShowErrors(true);
      setSaveError('Select whether a murmur is present or absent for the mitral and aortic valves.');
      return;
    }
    setSaveError('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}participant/update-cardiac`,
        { participant_id: participantId, cardiac_auscultation: data },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error saving cardiac auscultation data:', error);
      setSaveError(error.response?.data?.error || 'Could not save cardiac auscultation data.');
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    onSaveSuccess();
  };

  if (isLoading) {
    return <div>Loading cardiac auscultation data...</div>;
  }

  return (
    <CardiacAuscultationContainer>
      <SectionContainer>
        <SectionTitle>CARDIAC AUSCULTATION</SectionTitle>
        <ContentBox>
          <ValveComponent valve="mitral_valve" title="Mitral Valve" data={data.mitral_valve} onChange={handleChange} showErrors={showErrors} />
          <ValveComponent valve="aortic_valve" title="Aortic Valve" data={data.aortic_valve} onChange={handleChange} showErrors={showErrors} />
          <ValveComponent valve="axilla" title="Axilla" data={data.axilla} onChange={handleChange} showErrors={showErrors} />
        </ContentBox>
      </SectionContainer>
      {saveError && <FieldError>{saveError}</FieldError>}
      <SaveButton onClick={handleSaveAndNext}>Save and Next</SaveButton>
      <SuccessModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="Success"
        message={
          <p>
            Cardiac Auscultation data successfully saved for Participant ID: {participantId}
          </p>
        }
      />
    </CardiacAuscultationContainer>
  );
};

export default CardiacAuscultation;
